/**
 * iOS Safari / Chrome - Back-Forward Cache (bfcache) Image Fix
 *
 * Problem:
 * On iOS, when a user taps a product card → goes to PDP → hits Back,
 * iOS restores the page from bfcache. lazySizes' pageshow handler only
 * re-triggers images still in `.lazyloading` state, but iOS WebKit can
 * blank out images that were already loaded (especially those using
 * position:absolute + object-fit:cover inside an overflow:hidden container).
 *
 * Fix:
 * On `pageshow` with `event.persisted === true`, force-reload every product
 * card image's src/srcset so WebKit re-paints them correctly.
 */

(function () {
  'use strict';

  function isIOS() {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  /**
   * Force reload all product card images after bfcache restore.
   * We reassign src/srcset so WebKit re-fetches from its memory/disk cache
   * and re-paints the image inside its container.
   */
  function fixProductCardImages() {
    // Target all images inside product card featured image links
    var selectors = [
      '.product-card--featured-image-link img',
      '.product-card--featured-image img',
      'product-card img'
    ];

    var images = document.querySelectorAll(selectors.join(', '));

    images.forEach(function (img) {
      // 1. If image is still in lazyload/lazyloading state, kick lazySizes
      if (img.classList.contains('lazyload') || img.classList.contains('lazyloading')) {
        // Re-add lazyload class to trigger lazySizes re-evaluation
        img.classList.remove('lazyloaded', 'lazyloading');
        img.classList.add('lazyload');

        if (img.dataset.srcset) {
          img.removeAttribute('srcset');
        }
        if (img.dataset.src) {
          img.removeAttribute('src');
        }

        // Trigger lazySizes if available
        if (window.lazySizes) {
          window.lazySizes.loader.unveil(img);
        }
        return;
      }

      // 2. Image was already loaded — force WebKit to re-paint it
      // by briefly blanking and restoring src/srcset
      var currentSrc = img.src;
      var currentSrcset = img.srcset;

      // Only fix images that have a valid src (not data: or empty)
      if (!currentSrc || currentSrc.indexOf('data:') === 0) return;

      // Use a blank gif trick: set to blank then restore on next frame
      // This forces WebKit to re-decode and paint the image
      var blankSrc = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

      img.src = blankSrc;

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (currentSrcset) {
            img.srcset = currentSrcset;
          }
          img.src = currentSrc;

          // Ensure naturalWidth check after restore
          img.decode && img.decode().catch(function () {
            // decode can fail on Safari for already-cached images; ignore
          });
        });
      });
    });
  }

  /**
   * Also handle the case where the image container has
   * display:none via CSS on secondary images – re-trigger hover state reset
   */
  function resetProductCardState() {
    var cards = document.querySelectorAll('product-card, .product-card');
    cards.forEach(function (card) {
      // Remove any stuck hover classes that might have been frozen by bfcache
      card.classList.remove('is-hovered');

      // Reset secondary images display
      var secondaryImgs = card.querySelectorAll('.product-secondary-image');
      secondaryImgs.forEach(function (img) {
        if (!img.classList.contains('hover')) {
          img.style.display = '';
        }
      });
    });
  }

  /**
   * Main handler — runs on bfcache restore (persisted pageshow)
   */
  function onPageShow(event) {
    if (!event.persisted) return;

    // Small delay to let bfcache fully restore DOM before we manipulate it
    setTimeout(function () {
      resetProductCardState();
      fixProductCardImages();
    }, 50);

    // Second pass for slower devices
    setTimeout(function () {
      fixProductCardImages();
    }, 300);
  }

  // iOS-specific: also handle visibility change (Chrome on iOS uses this)
  function onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // Check if we're coming back via navigation (performance entries)
      var navEntries = performance && performance.getEntriesByType &&
        performance.getEntriesByType('navigation');
      if (navEntries && navEntries[0] && navEntries[0].type === 'back_forward') {
        setTimeout(fixProductCardImages, 100);
      }
    }
  }

  // Always attach pageshow (works on all browsers)
  window.addEventListener('pageshow', onPageShow);

  // iOS Chrome additional fallback
  if (isIOS()) {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

})();